<template>
  <div id="bulk-select-targets" class="flex w-screen">
    <div id="bulk-table" class="relative flex flex-col w-full">
      <div class="flex items-center gap-4 px-6 py-4">
        <BlInputSearch
          class="w-80"
          :input="searchInput"
          @changed="onInputSearch"
        />
        <div class="w-40">
          <BlInputFilter :input="filterInput" @changed="onChangeFilter" />
        </div>
        <BlSort
          class="w-60"
          v-model="sortInput"
          :options="sortOptions"
          :defaultValue="{ primary: 'date', secondary: 'desc' }"
          @change="onChangeSort($event)"
        />
      </div>
      <div v-if="pageLoading" class="grid h-128 place-items-center">
        <div class="blum-loader"></div>
      </div>
      <div
        v-else-if="!centerTargets.length"
        class="flex flex-col items-center justify-center h-128"
      >
        <BlTextBody1
          class="text-center"
          text="Currently, there are no targets to display."
        />
      </div>
      <div
        v-else
        class="flex flex-col gap-6 px-6 pb-48 overflow-y-auto"
        :style="{ height: 'calc(100vh - 18.5rem)' }"
      >
        <div class="relative border rounded border-slate-400">
          <div
            class="sticky top-0 z-10 flex items-center flex-grow-0 w-full bg-slate-300"
          >
            <div
              class="flex items-center flex-shrink-0 h-12 gap-2 px-4 border-r rounded-tl border-slate-400"
              :style="{ width: '20%' }"
            >
              <BlInputCheck
                :indeterminate="checkedTargets.length > 0"
                :checked="checkedAll"
                @changed="onCheckAllTargets()"
              />
              <BlTextSubtitle3 text="Name" />
            </div>
            <div
              class="flex items-center flex-shrink-0 h-12 px-4"
              :style="{ width: '15%' }"
            >
              <BlTextSubtitle3 text="Curriculum" />
            </div>
            <div
              class="flex items-center flex-shrink-0 h-12 px-4"
              :style="{ width: '25%' }"
            >
              <BlTextSubtitle3 text="Description" />
            </div>
            <div
              class="flex flex-no-wrap items-center flex-shrink-0 h-12 px-4"
              :style="{ width: '15%' }"
            >
              <BlTextSubtitle3
                class="flex-shrink-0"
                :style="{ width: 'max-content' }"
                text="Data collection method"
              />
            </div>
            <div
              class="flex items-center flex-shrink-0 h-12 px-4"
              :style="{ width: '25%' }"
            >
              <BlTextSubtitle3 :text="`Goal & success metric`" />
            </div>
          </div>
          <DatabankItem
            v-for="target in centerTargets"
            :key="target.id"
            :panelId="panelId"
            :target="target"
            useCurriculum
            useAddButton
            useCheckbox
            :isAdded="!!selectedTargets.find(i => i.id === target.id)"
            @select="$emit('open-panel', target)"
            @add="onAddSelected(target)"
            :isChecked="!!checkedTargets.find(i => i.id === target.id)"
            @check="onToggleCheckTarget(target)"
          />
        </div>
        <BlInputPagination
          v-if="totalCenterTargets > 25"
          :page="page"
          :perPage="perPage"
          :total="totalCenterTargets"
          @changed="changePage"
          @changed-per-page="changePerPage"
        />
      </div>

      <div
        class="absolute z-10 transition-all duration-500"
        :class="[checkedTargets.length > 0 ? 'bottom-10' : '-bottom-100']"
        :style="{ left: 'calc((100% - 50rem) / 2)' }"
      >
        <div
          class="flex items-center justify-between h-16 px-6 border rounded shadow-md bg-pure-white border-slate-400"
          :style="{ width: 'calc(50rem)' }"
        >
          <div>
            <BlTextSubtitle3
              :text="`${checkedTargets.length} target(s) selected`"
            />
          </div>
          <div class="flex items-center gap-2">
            <BlInputButton
              :label="`Deselect all`"
              :title="`Deselect all`"
              type="outlined"
              dense
              @clicked="checkedTargets = []"
            />
            <BlInputButton
              :label="`Add ${checkedTargets.length} target(s)`"
              dense
              @clicked="onAddCheckedTargets()"
            />
          </div>
        </div>
      </div>
    </div>

    <div
      id="bulk-selected"
      class="relative flex-shrink-0 pb-32 overflow-y-auto border w-92 bg-pure-white border-slate-400"
      :style="{ height: 'calc(100vh - 14rem)' }"
    >
      <div
        class="sticky top-0 z-10 flex flex-col gap-1 px-6 py-5 border-b bg-pure-white border-slate-400"
      >
        <BlTextSubtitle1 text="List of selected targets" />
        <BlTextBody2
          text="Here is a list of targets you've selected to be added for the client."
        />
      </div>
      <div v-if="!selectedTargets.length" class="grid h-64 place-items-center">
        <BlTextBody2 text="No target has been added yet." />
      </div>
      <div
        v-else
        v-for="target in selectedTargets"
        :key="target.id"
        class="flex items-center justify-between gap-3 px-6 py-4 border-b border-slate-400"
      >
        <div class="flex flex-col gap-1 truncate">
          <BlTextBody2
            class="truncate"
            :text="target.curriculum_name"
            :title="target.curriculum_name"
          />
          <BlTextSubtitle3
            class="truncate text-slate-1000"
            :text="target.name"
            :title="target.name"
          />
          <BlTextBody2 :text="targetTypes[target.type]" />
        </div>
        <BlInputButton
          class="flex-shrink-0"
          type="outlined-error"
          dense
          icon="delete"
          @clicked="$emit('remove', target)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import debounce from "lodash/debounce";

import DatabankItem from "../../../repository/databank/Item.vue";

import BlTextSubtitle1 from "../../../BlTextSubtitle1.vue";
import BlTextSubtitle3 from "../../../BlTextSubtitle3.vue";
import BlTextBody1 from "../../../BlTextBody1.vue";
import BlTextBody2 from "../../../BlTextBody2.vue";
import BlInputSearch from "../../../BlInputSearch.vue";
import BlInputFilter from "../../../BlInputFilter.vue";
import BlSort from "../../../BlSort.vue";
import BlInputCheck from "../../../BlInputCheck.vue";
import BlInputButton from "../../../BlInputButton.vue";
import BlInputPagination from "../../../BlInputPagination.vue";

export default {
  name: "SelectTargetDatabank",
  components: {
    DatabankItem,
    BlTextSubtitle1,
    BlTextSubtitle3,
    BlTextBody1,
    BlTextBody2,
    BlInputSearch,
    BlInputFilter,
    BlSort,
    BlInputCheck,
    BlInputButton,
    BlInputPagination
  },
  props: {
    panelId: { type: String },
    selectedTargets: { type: Array, default: () => [] },
    targetTypes: { type: Object }
    // @open-panel
    // @add
    // @remove
    // @add-multi
  },
  data() {
    const defaultFilter = [[], []];
    return {
      pageLoading: true,
      page: 1,
      perPage: 25,
      searchInput: {
        placeholder: "Search target by name",
        model: ""
      },
      defaultFilter,
      filterInput: {
        model: defaultFilter,
        tabs: [
          {
            name: "Curriculum",
            kind: "multi",
            options: [],
            url: `/api/v1/curriculums?sort=name_asc&query=`,
            fetchPage: 1,
            dataKey: "curriculums",
            countKey: "total_count",
            optionKeys: { value: "id", label: "name", prefixColor: "color" }
          },
          {
            name: "Method",
            kind: "multi",
            options: [
              { name: "Percentage", value: "Target::Percentage" },
              { name: "Duration", value: "Target::Duration" },
              { name: "Partial interval recording", value: "Target::Pir" },
              { name: "Frequency", value: "Target::Frequency" },
              { name: "Prompting", value: "Target::Prompting" },
              { name: "Skill-Based Treatment (SBT)", value: "Target::Sbt" }
            ]
          }
        ]
      },
      sortInput: { primary: "date", secondary: "desc" },
      sortOptions: {
        primary: [
          { value: "name", label: "Name" },
          { value: "curriculum", label: "Curriculum" },
          { value: "type", label: "Data collection method" },
          { value: "date", label: "Date created" }
        ],
        secondary: [
          { value: "asc", label: "Ascending" },
          { value: "desc", label: "Descending" }
        ]
      },
      checkedTargets: []
    };
  },
  computed: {
    ...mapGetters(["centerTargets", "totalCenterTargets"]),
    getParams() {
      let params = `?page=${this.page}&per_page=${this.perPage}`;
      const query = this.searchInput.model;
      const curriculumIds = this.filterInput.model
        .at(0)
        .map(a => a.value)
        .join(",");
      const type = this.filterInput.model
        .at(1)
        .map(a => a.value)
        .join(",");
      const sort = this.sortInput;
      if (query) params += `&query=${query}`;
      if (curriculumIds) params += `&curriculum_ids=${curriculumIds}`;
      if (type) params += `&type=${type}`;
      // - sort mapping -
      // name, ascending -> name_asc
      // name, descending -> name_desc
      // curriculum, ascending -> curriculum_asc
      // curriculum, descending -> curriculum_desc
      // data collection method, ascending -> type_asc
      // data collection method, descending -> type_desc
      // date created, ascending -> oldest
      // date created, descending -> newest
      if (sort.primary === "date") {
        params += `&sort=${sort.secondary === "desc" ? "newest" : "oldest"}`;
      } else {
        params += `&sort=${sort.primary}_${sort.secondary}`;
      }
      return params;
    },
    checkedAll() {
      const checkedCount = this.checkedTargets.length;
      const selectedIds = this.selectedTargets.map(i => i.id);
      const unselectTargets = this.centerTargets.filter(
        i => !selectedIds.includes(i.id)
      );
      return checkedCount > 0 && checkedCount === unselectTargets.length;
    }
  },
  mounted() {
    this.pageLoading = true;
    this.fetchTargets();
  },
  methods: {
    ...mapActions(["getTargets"]),
    onInputSearch(payload) {
      payload.input.model = payload.value;
      this.onSearching();
    },
    onSearching: debounce(function() {
      this.page = 1;
      this.fetchTargets();
    }, 1000),
    onChangeFilter(payload) {
      payload.input.model = payload.value;
      this.page = 1;
      this.fetchTargets();
    },
    onChangeSort(val) {
      const { primary, secondary } = this.sortInput;
      if (val.primary !== primary || val.secondary !== secondary) {
        this.sortInput = val;
        this.page = 1;
        this.fetchTargets();
      }
    },
    changePage(page) {
      if (page === this.page) return;
      this.page = page;
      this.fetchTargets();
    },
    changePerPage(perPage) {
      if (perPage === this.perPage) return;
      this.page = 1;
      this.perPage = perPage;
      this.fetchTargets();
    },
    fetchTargets() {
      this.pageLoading = true;
      this.getTargets({ kind: "center", params: this.getParams }).then(() => {
        this.pageLoading = false;
        this.checkedTargets = [];
      });
    },
    //
    async onAddSelected(target) {
      this.checkedTargets = this.checkedTargets.filter(i => i.id !== target.id);
      this.$emit("add", target);
    },
    onToggleCheckTarget(target) {
      const found = this.checkedTargets.find(i => i.id === target.id);
      if (found) {
        this.checkedTargets = this.checkedTargets.filter(
          i => i.id !== target.id
        );
      } else {
        this.checkedTargets.push(target);
      }
    },
    onCheckAllTargets() {
      const checkedCount = this.checkedTargets.length;
      const selectedIds = this.selectedTargets.map(i => i.id);
      const unselectTargets = this.centerTargets.filter(
        target => !selectedIds.includes(target.id)
      );
      if (checkedCount < unselectTargets.length) {
        this.checkedTargets = unselectTargets;
      } else {
        this.checkedTargets = [];
      }
    },
    onAddCheckedTargets() {
      this.$emit("add-multi", this.checkedTargets);
      this.checkedTargets = [];
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/clients/target/modal/SelectTargetDatabank.vue